import type { HTMLProps, RefCallback } from 'react'
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react'

import { useControllableState } from '../hooks/useControllableState'
import type { PopoverProps } from '../Popover/Popover'
import { Popover } from '../Popover/Popover'
import type { PopoverState } from '../Popover/popoverTypes'
import { mergeRefs } from '../utils/mergeRefs'
import { useOnNestedClickOutside } from '../utils/useOnNestedClickOutside'

type EventHandlers = Partial<
  Pick<
    HTMLProps<HTMLOrSVGElement>,
    'onPointerEnter' | 'onPointerLeave' | 'onPointerDown' | 'onClickCapture'
  >
>

type PopoverOnHoverRendererArgs = {
  open: () => void
  close: () => void
  isOpen: boolean
  props: EventHandlers
}

export type PopoverOnHoverRef = {
  open: () => void
  close: () => void
}

export type PopoverOnHoverProps = {
  open?: boolean
  defaultOpen?: boolean
  onIsOpenChange?: (open: boolean) => void
  disabled?: boolean

  allowContentHover?: boolean
  closeOnClick?: boolean
  /** Toggle popover on touch tap (mobile). Desktop hover behavior is unchanged. */
  openOnTouch?: boolean

  openDelay?: number
  closeDelay?: number

  /**
   * The element that will be displayed initially and on which the
   * popover behaviour will be installed.
   *
   * The arguments given to the render function are to be used in the
   * direct child for the popover to work.
   *
   * Example:
   *
   * ```jsx
   * <TooltipOnHover
   *   // other props
   *   target={({ props, ref }) => {
   *     return <div ref={ref} {...props}>Hello World</div>
   *   }}
   * />
   * ```
   */
  target: (
    args: PopoverOnHoverRendererArgs & { ref: RefCallback<HTMLOrSVGElement> }
  ) => React.ReactNode
  content: (
    args: PopoverOnHoverRendererArgs &
      PopoverState & { ref: RefCallback<HTMLOrSVGElement> }
  ) => React.ReactNode

  zIndex?: string
  containerStyle?: Partial<CSSStyleDeclaration>
  ref?: React.Ref<PopoverOnHoverRef>
} & Omit<
  PopoverProps,
  'isOpen' | 'children' | 'content' | 'containerStyle' | 'target' | 'ref'
>

export function PopoverOnHover({
  open,
  defaultOpen,
  onIsOpenChange,

  allowContentHover,
  closeOnClick,
  openOnTouch,

  openDelay = 300,
  closeDelay = allowContentHover ? 200 : 50,

  target,
  content,
  padding = 8,
  positions = ['bottom'],
  align = 'start',
  zIndex = '7',
  containerStyle = {},
  reposition = true,
  boundaryInset = 8,
  boundaryElement,
  disabled,
  ref: controlRef,
  ...props
}: PopoverOnHoverProps) {
  const [isOpen = false, setIsOpen] = useControllableState<boolean>({
    prop: open,
    defaultProp: defaultOpen,
    onChange: onIsOpenChange,
  })

  const openTimerRef = useRef(0)
  const closeTimerRef = useRef(0)
  const touchedRef = useRef(false)

  const handleOpen = useCallback(() => {
    clearTimeout(closeTimerRef.current)
    setIsOpen(true)
  }, [setIsOpen])

  const handleClose = useCallback(() => {
    clearTimeout(openTimerRef.current)
    setIsOpen(false)
  }, [setIsOpen])

  const handlePointerEnter = useCallback(
    (e: React.PointerEvent<HTMLOrSVGElement>) => {
      if (e.pointerType === 'touch') return
      clearTimeout(closeTimerRef.current)
      openTimerRef.current = window.setTimeout(() => setIsOpen(true), openDelay)
    },
    [openDelay, setIsOpen]
  )

  const handlePointerLeave = useCallback(
    (e: React.PointerEvent<HTMLOrSVGElement>) => {
      if (e.pointerType === 'touch') return
      clearTimeout(openTimerRef.current)
      closeTimerRef.current = window.setTimeout(
        () => setIsOpen(false),
        closeDelay
      )
    },
    [closeDelay, setIsOpen]
  )

  const {
    ref: contentRef,
    targetRef,
    ChildRegistrationContext,
  } = useOnNestedClickOutside({
    onClickOutside() {
      handleClose()
    },
    disabled: !isOpen,
  })

  useImperativeHandle(
    controlRef,
    () => ({
      open: handleOpen,
      close: handleClose,
    }),
    [handleOpen, handleClose]
  )

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLOrSVGElement>) => {
      if (openOnTouch && e.pointerType === 'touch') {
        e.preventDefault()
        e.stopPropagation()
        touchedRef.current = true
        setTimeout(() => {
          touchedRef.current = false
        }, 100)
        clearTimeout(openTimerRef.current)
        clearTimeout(closeTimerRef.current)
        setIsOpen((prev) => !prev)
        return
      }
      if (closeOnClick) {
        handleClose()
      }
    },
    [openOnTouch, closeOnClick, handleClose, setIsOpen]
  )

  const handleClickCapture = useCallback(
    (e: React.MouseEvent<HTMLOrSVGElement>) => {
      if (touchedRef.current) {
        e.preventDefault()
        e.stopPropagation()
        touchedRef.current = false
      }
    },
    []
  )

  const hoverHandlers: EventHandlers = {
    onPointerEnter: handlePointerEnter,
    onPointerLeave: handlePointerLeave,
  }

  const targetHandlers: EventHandlers = {
    ...hoverHandlers,
    onPointerDown: openOnTouch || closeOnClick ? handlePointerDown : undefined,
    onClickCapture: openOnTouch ? handleClickCapture : undefined,
  }

  // cleanup any queued state updates on unmount
  useEffect(() => {
    return () => {
      clearTimeout(openTimerRef.current)
      clearTimeout(closeTimerRef.current)
    }
  }, [])

  return (
    <ChildRegistrationContext>
      <Popover<HTMLOrSVGElement>
        {...props}
        padding={padding}
        positions={positions}
        align={align}
        boundaryInset={boundaryInset}
        isOpen={isOpen && !disabled}
        content={(args) =>
          content({
            ref: contentRef,
            ...args,
            props: allowContentHover ? hoverHandlers : {},
            open: handleOpen,
            close: handleClose,
            isOpen,
          })
        }
        reposition={reposition}
        containerStyle={{
          ...containerStyle,
          zIndex,
        }}
        target={({ ref }) =>
          target({
            ref: mergeRefs(ref, targetRef),
            props: targetHandlers,
            isOpen,
            close: handleClose,
            open: handleOpen,
          })
        }
      />
    </ChildRegistrationContext>
  )
}
